import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { resetSession } from "./resetSession";

/**
 * `import.meta.env.DEV` is replaced at build time, so this is tree-shaken out of production
 * rather than hidden behind a flag (#16, #17). The accounts are asked of the API, which also
 * buys the column that matters: what each holds now, beside what the fixture promised.
 */
type DevAccount = {
  persoonId: string;
  name: string;
  code: string;
  korpsAccess: KorpsAccess;
  isOndersteuner: boolean;
  isBeheerder: boolean;
  exists: boolean;
  liveKorpsCount: number;
  liveHasBevoegdheid: boolean;
  liveIsBeheerder: boolean;
};

type KorpsAccess = "None" | "OneKorps" | "TwoKorpsen" | "ViaVeiligheidsregio";

const devAccountsKey = ["dev", "accounts"] as const;

export function DevSignIn() {
  const { t } = useTranslation("session");
  const queryClient = useQueryClient();

  const accounts = useQuery({
    queryKey: devAccountsKey,
    queryFn: async (): Promise<DevAccount[]> => {
      const response = await fetch("/api/dev/accounts", {
        credentials: "same-origin",
      });
      if (!response.ok) {
        throw new Error(t("dev.errors.accounts", { status: response.status }));
      }
      return (await response.json()) as DevAccount[];
    },
    enabled: import.meta.env.DEV,
    staleTime: Infinity,
    retry: false,
  });

  const signIn = useMutation({
    mutationFn: async (persoonId: string) => {
      const response = await fetch(`/api/dev/sign-in?persoonId=${persoonId}`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!response.ok) {
        throw new Error(t("dev.errors.signIn", { status: response.status }));
      }
    },
    onSuccess: async () => {
      // The whole cache, not just the session: every query here is answered for whoever holds
      // the cookie, and the ones that 401'd while signed out would otherwise stay 401'd. It
      // takes the panel's own live columns with it, which is what a re-seed needs anyway.
      await resetSession(queryClient);
    },
  });

  if (!import.meta.env.DEV) {
    return null;
  }

  const error = accounts.error ?? signIn.error;

  return (
    <section
      data-testid="dev-signin"
      className="mt-6 overflow-hidden rounded-md border border-dashed"
    >
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-dashed bg-muted/50 px-3 py-2">
        <span className="rounded-sm bg-foreground px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-widest text-background uppercase">
          {t("dev.badge")}
        </span>
        <h2 className="font-mono text-xs font-semibold">{t("dev.title")}</h2>
      </header>

      <AccountsContent
        isPending={accounts.isPending}
        accounts={accounts.data}
        signInPending={signIn.isPending}
        signInVariables={signIn.variables}
        onSignIn={(persoonId) => signIn.mutate(persoonId)}
      />

      {error && (
        <p className="border-t border-dashed px-3 py-2 font-mono text-xs text-destructive">
          {error.message}
        </p>
      )}
    </section>
  );
}

/** The loading / empty / table split, kept separate so the sign-in card stays a plain switch. */
function AccountsContent({
  isPending,
  accounts,
  signInPending,
  signInVariables,
  onSignIn,
}: {
  isPending: boolean;
  accounts: DevAccount[] | undefined;
  signInPending: boolean;
  signInVariables: string | undefined;
  onSignIn: (persoonId: string) => void;
}) {
  const { t } = useTranslation("session");

  if (isPending) {
    return (
      <p className="px-3 py-4 font-mono text-xs text-muted-foreground">
        {t("dev.loading")}
      </p>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <p className="px-3 py-4 font-mono text-xs text-muted-foreground">
        {t("dev.empty")}
      </p>
    );
  }

  return (
    <AccountsTable
      accounts={accounts}
      signInPending={signInPending}
      signInVariables={signInVariables}
      onSignIn={onSignIn}
    />
  );
}

// Both axes. The panel sits inside the sign-in card, which is narrower than five columns
// of monospace, and a debug readout with its last column clipped off is not one.
function AccountsTable({
  accounts,
  signInPending,
  signInVariables,
  onSignIn,
}: {
  accounts: DevAccount[];
  signInPending: boolean;
  signInVariables: string | undefined;
  onSignIn: (persoonId: string) => void;
}) {
  return (
    <div className="max-h-96 overflow-auto">
      <table className="w-full border-collapse font-mono text-xs">
        <AccountsTableHead />
        <tbody>
          {accounts.map((account) => (
            <AccountRow
              key={account.persoonId}
              account={account}
              busy={signInPending && signInVariables === account.persoonId}
              disabled={signInPending || !account.exists}
              onSignIn={onSignIn}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountsTableHead() {
  const { t } = useTranslation("session");

  return (
    <thead className="sticky top-0 bg-background/95 text-left text-muted-foreground">
      <tr className="border-b">
        <th scope="col" className="px-2 py-1.5 font-medium">
          {t("dev.columns.persoon")}
        </th>
        <th scope="col" className="px-2 py-1.5 font-medium">
          {t("dev.columns.korpsen")}
        </th>
        {/* Abbreviated, with the word itself on the header. Five columns of monospace do
            not fit the sign-in card, and the two flags are the ones a code beside every
            row already spells out. */}
        <th
          scope="col"
          className="px-2 py-1.5 text-center font-medium"
          title={t("dev.columns.ondersteuner")}
        >
          {t("dev.columns.ondersteunerShort")}
        </th>
        <th
          scope="col"
          className="px-2 py-1.5 text-center font-medium"
          title={t("dev.columns.beheerder")}
        >
          {t("dev.columns.beheerderShort")}
        </th>
        <th scope="col" className="px-2 py-1.5 text-right font-medium">
          {t("dev.columns.code")}
        </th>
      </tr>
    </thead>
  );
}

function AccountRow({
  account,
  busy,
  disabled,
  onSignIn,
}: {
  account: DevAccount;
  busy: boolean;
  disabled: boolean;
  onSignIn: (persoonId: string) => void;
}) {
  const { t } = useTranslation("session");

  return (
    <tr className="border-b last:border-b-0">
      <td className="px-2 py-1 whitespace-nowrap">{account.name}</td>
      <td className="px-2 py-1 whitespace-nowrap">
        {t(`dev.korpsAccess.${account.korpsAccess}`)}
        <Korpsen
          expected={expectedKorpsCount(account.korpsAccess)}
          actual={account.liveKorpsCount}
          // The regio cell is the one whose number is not in its label, and the one
          // the label cannot give: how many korpsen a regio holds is the regio's
          // business. Without it "via regio" is indistinguishable from "één korps".
          toon={account.korpsAccess === "ViaVeiligheidsregio"}
          hint={t("dev.drift")}
        />
      </td>
      <td className="px-2 py-1 text-center">
        <Flag
          expected={account.isOndersteuner}
          actual={account.liveHasBevoegdheid}
          hint={t("dev.drift")}
        />
      </td>
      <td className="px-2 py-1 text-center">
        <Flag
          expected={account.isBeheerder}
          actual={account.liveIsBeheerder}
          hint={t("dev.drift")}
        />
      </td>
      <td className="px-2 py-1 text-right">
        {/* The cell code is the button. It is the shortest label that says which of
            the sixteen this is, and it matches the suffix of the name beside it. */}
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 font-mono text-[11px]"
          data-testid={`dev-signin-${account.code}`}
          aria-label={t("dev.signInAs", { persoon: account.name })}
          disabled={disabled}
          onClick={() => onSignIn(account.persoonId)}
        >
          {busy ? t("dev.busy") : account.exists ? account.code : t("dev.missing")}
        </Button>
      </td>
    </tr>
  );
}

/** How many korpsen the cell promises, so the live count has something to be measured against. */
function expectedKorpsCount(korpsAccess: KorpsAccess): number {
  switch (korpsAccess) {
    case "OneKorps":
      return 1;
    case "TwoKorpsen":
      return 2;
    case "ViaVeiligheidsregio":
      // At least one, and how many is the regio's business rather than the matrix's - so the
      // drift marker asks only whether the grant is still there.
      return 1;
    default:
      return 0;
  }
}

/**
 * Drift earns a column: an account silently demoted is indistinguishable from a permission bug
 * in the screen you were actually debugging.
 */
function Flag({
  expected,
  actual,
  hint,
}: {
  expected: boolean;
  actual: boolean;
  hint: string;
}) {
  return (
    <span
      className={expected === actual ? undefined : "text-destructive"}
      title={expected === actual ? undefined : hint}
    >
      {actual ? "✓" : "·"}
      {expected === actual ? "" : "!"}
    </span>
  );
}

/** Short of the promised count is drift; a regio grant has no fixed promise beyond "some". */
function isKorpsenDrifted(expected: number, actual: number): boolean {
  return expected === 0 ? actual !== 0 : actual < expected;
}

/** Bundles the className/title/suffix triple, since all three follow the same one branch. */
function driftMarker(drifted: boolean, hint: string) {
  return drifted
    ? { className: "ml-1 text-destructive", title: hint, suffix: "!" }
    : { className: "ml-1 text-muted-foreground", title: undefined, suffix: "" };
}

/**
 * The korps count beside the label: always for a regio grant, and otherwise only when it falls
 * short of what the cell promises.
 */
function Korpsen({
  expected,
  actual,
  toon,
  hint,
}: {
  expected: number;
  actual: number;
  toon: boolean;
  hint: string;
}) {
  const drifted = isKorpsenDrifted(expected, actual);

  if (!drifted && !toon) {
    return null;
  }

  const marker = driftMarker(drifted, hint);

  return (
    <span className={marker.className} title={marker.title}>
      ({actual}
      {marker.suffix})
    </span>
  );
}
